/**
 * 親ウィンドウへのpostMessage送信を管理するComposable
 *
 * - CHAT_EMBED_ORIGIN_URL（カンマ区切りで複数指定可）を許可オリジンリストとしてパースする
 * - document.referrer から親オリジンを検出し、許可リストに含まれる場合のみ postMessage を送信する
 * - 許可リストに含まれない場合は送信しない（セキュリティ重視）
 */
export const useParentMessaging = () => {
    const config = useRuntimeConfig();

    /**
     * 環境変数 CHAT_EMBED_ORIGIN_URL をカンマ区切りでパースし、許可オリジンリストを生成
     */
    const allowedOrigins = computed<string[]>(() => {
        const raw = config.public.chatEmbedOriginUrl as string | undefined;
        if (!raw) return [];
        return raw
            .split(',')
            .map((origin: string) => origin.trim())
            .filter((origin: string) => origin.length > 0);
    });

    /**
     * document.referrer から親ページのオリジンを検出し、許可リストと照合して解決済みオリジンを返す
     * - クライアントサイドでのみ動作（SSR時は null）
     * - 許可リストに含まれない場合も null
     */
    const resolvedOrigin = computed<string | null>(() => {
        if (!import.meta.client) return null;

        try {
            const referrer = document.referrer;
            if (!referrer) return null;

            const parentOrigin = new URL(referrer).origin;
            console.info(`親オリジンは "${parentOrigin}"。許可リスト:`, allowedOrigins.value);

            if (allowedOrigins.value.includes(parentOrigin)) {
                return parentOrigin;
            }


            console.warn(
                `[useParentMessaging] 親オリジン "${parentOrigin}" は許可リストに含まれていません。許可リスト:`,
                allowedOrigins.value
            );
            return null;
        } catch {
            console.warn('[useParentMessaging] 親オリジンの検出に失敗しました');
            return null;
        }
    });

    /**
     * 親ウィンドウにメッセージを送信する
     * resolvedOrigin が存在する場合のみ postMessage を実行する
     */
    const sendToParent = (data: object): void => {
        if (!import.meta.client) return;

        const origin = resolvedOrigin.value;
        if (!origin) {
            console.warn('[useParentMessaging] 有効な親オリジンが検出されなかったため、postMessage を送信しませんでした', data);
            return;
        }

        window.parent.postMessage(data, origin);
    };

    return {
        allowedOrigins,
        resolvedOrigin,
        sendToParent,
    };
};
